import React, { useState, useEffect, useCallback } from "react";
import API_BASE from "../apiConfig";
import { useTheme, getT } from "../ThemeContext";
import { useAppConfig } from "../AppContext";
import { useDateTimeFormat } from "../utils/formatters";
import { PrimaryButton } from "../components/ThemedButton";

const money = (n, currency = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency }).format(Number(n || 0));

async function api(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.message || `Request failed (${res.status})`);
  }
  return data;
}

export default function POSCustomers({ currentUser }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const { config } = useAppConfig();
  const currency = (config?.currency || "USD").toUpperCase();
  const { fmtDateTime } = useDateTimeFormat();

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null); // {customer_type, ref_id, ...}
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [showWalkin, setShowWalkin] = useState(false);
  const [walkinName, setWalkinName] = useState("");
  const [walkinPhone, setWalkinPhone] = useState("");
  const [walkinEmail, setWalkinEmail] = useState("");
  const [walkinBusy, setWalkinBusy] = useState(false);
  const [walkinError, setWalkinError] = useState("");

  useEffect(() => {
    if (!query) { setResults([]); return; }
    const t = setTimeout(() => {
      api(`/pos/customers/search?q=${encodeURIComponent(query)}`)
        .then((d) => setResults(d.results || []))
        .catch((e) => setError(e.message));
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const openDetail = useCallback(async (r) => {
    setError(""); setDetail(null); setSelected(r); setBusy(true);
    try {
      // resolve (creating if needed) the pos_customers_mapping row, then fetch full detail
      const resolved = await api(
        `/pos/customers/resolve?customer_type=${r.customer_type}&ref_id=${r.ref_id}`,
        { method: "POST" }
      );
      const d = await api(`/pos/customers/${resolved.mapping_id}`);
      setDetail(d.customer);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }, []);

  async function createWalkin() {
    if (!walkinName.trim()) { setWalkinError("Name is required."); return; }
    setWalkinBusy(true); setWalkinError("");
    try {
      const d = await api("/pos/customers/walkin", {
        method: "POST",
        body: JSON.stringify({
          display_name: walkinName.trim(),
          phone: walkinPhone || null,
          email: walkinEmail || null,
        }),
      });
      const full = await api(`/pos/customers/${d.mapping_id}`);
      setDetail(full.customer);
      setSelected({ customer_type: "walkin", ref_id: null, display_name: walkinName.trim() });
      setShowWalkin(false);
      setWalkinName(""); setWalkinPhone(""); setWalkinEmail("");
      setQuery(""); setResults([]);
    } catch (e) { setWalkinError(e.message); } finally { setWalkinBusy(false); }
  }

  const card = { background: T.glass, border: `1px solid ${T.glassBdr}`, borderRadius: 14, padding: 16 };
  const input = { width: "100%", padding: 8, marginBottom: 10, borderRadius: 8, border: `1px solid ${T.inputBdr}`, background: T.inputBg, color: T.inputText, boxSizing: "border-box" };

  return (
    <div style={{ color: T.textPrimary, display: "flex", gap: 16, flexWrap: "wrap" }}>
      {/* LEFT — search & results */}
      <div style={{ flex: "1 1 360px", minWidth: 320 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h2 style={{ margin: 0 }}>Customers</h2>
          <PrimaryButton onClick={() => setShowWalkin((s) => !s)}>
            {showWalkin ? "Cancel" : "+ New Walk-in Customer"}
          </PrimaryButton>
        </div>

        {showWalkin && (
          <div style={{ ...card, marginBottom: 14 }}>
            <h3 style={{ marginTop: 0, fontSize: 15 }}>New Walk-in Customer</h3>
            {walkinError && <div style={{ color: T.red, marginBottom: 8 }}>{walkinError}</div>}
            <label style={{ fontSize: 12, color: T.textSub }}>Display Name *</label>
            <input style={input} value={walkinName} onChange={(e) => setWalkinName(e.target.value)} placeholder="e.g. Jane Doe" />
            <label style={{ fontSize: 12, color: T.textSub }}>Phone</label>
            <input style={input} value={walkinPhone} onChange={(e) => setWalkinPhone(e.target.value)} placeholder="Optional" />
            <label style={{ fontSize: 12, color: T.textSub }}>Email</label>
            <input style={input} value={walkinEmail} onChange={(e) => setWalkinEmail(e.target.value)} placeholder="Optional" />
            <PrimaryButton disabled={walkinBusy} onClick={createWalkin}>
              {walkinBusy ? "Creating…" : "Create Walk-in Customer"}
            </PrimaryButton>
          </div>
        )}

        <input
          value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Search students / parents by name, email, phone…"
          style={{ ...input, fontSize: 14, padding: "10px 12px" }}
        />
        {error && <div style={{ color: T.red, marginBottom: 8 }}>{error}</div>}

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {results.map((r) => (
            <div
              key={`${r.customer_type}-${r.ref_id}`}
              style={{ ...card, cursor: "pointer", padding: 12, borderColor: selected?.ref_id === r.ref_id && selected?.customer_type === r.customer_type ? T.yellow : T.glassBdr }}
              onClick={() => openDetail(r)}
            >
              <div style={{ fontWeight: 700, fontSize: 13 }}>{r.display_name}</div>
              <div style={{ color: T.textSub, fontSize: 12, marginTop: 2 }}>{r.sub_label}</div>
              <div style={{ color: T.textMuted, fontSize: 11, marginTop: 2 }}>
                {r.email || ""}{r.email && r.phone ? " · " : ""}{r.phone || ""}
              </div>
            </div>
          ))}
          {!results.length && query && <div style={{ color: T.textMuted }}>No matches.</div>}
        </div>
      </div>

      {/* RIGHT — detail */}
      <div style={{ flex: "1 1 420px", minWidth: 360 }}>
        {busy && <div style={{ color: T.textMuted }}>Loading…</div>}
        {!busy && !detail && (
          <div style={{ ...card, color: T.textMuted, textAlign: "center", padding: 40 }}>
            Search and select a customer to view details, store credit, and purchase history.
          </div>
        )}
        {detail && (
          <div style={{ ...card }}>
            <h3 style={{ marginTop: 0 }}>{detail.display_name}</h3>
            <div style={{ fontSize: 12, color: T.textSub, marginBottom: 12 }}>
              {detail.customer_type === "student" ? "Student" : detail.customer_type === "parent" ? "Parent" : "Walk-in"}
              {detail.phone ? ` · ${detail.phone}` : ""}{detail.email ? ` · ${detail.email}` : ""}
            </div>

            <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
              <div style={{ flex: 1, background: T.tabActiveBg, borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 11, color: T.textSub }}>Store Credit</div>
                <div style={{ fontSize: 20, fontWeight: 900, color: T.yellow }}>
                  {money(detail.store_credit_balance, currency)}
                </div>
              </div>
              <div style={{ flex: 1, background: T.tabActiveBg, borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 11, color: T.textSub }}>Loyalty Points</div>
                <div style={{ fontSize: 20, fontWeight: 900 }}>{detail.loyalty_points ?? 0}</div>
              </div>
              <div style={{ flex: 1, background: T.tabActiveBg, borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 11, color: T.textSub }}>Total Spent</div>
                <div style={{ fontSize: 20, fontWeight: 900 }}>{money(detail.total_spent, currency)}</div>
              </div>
            </div>

            {detail.customer_type === "student" && detail.active_enrollments?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: T.textSub, marginBottom: 6 }}>Active Enrollments</div>
                {detail.active_enrollments.map((e) => (
                  <div key={e.enrollment_id} style={{ fontSize: 13, padding: "4px 0" }}>{e.batch_name}</div>
                ))}
              </div>
            )}

            {detail.customer_type === "parent" && detail.parent && (
              <div style={{ marginBottom: 16, fontSize: 12, color: T.textSub }}>
                {detail.parent.card_brand && detail.parent.card_last4
                  ? `Card on file: ${detail.parent.card_brand} •••• ${detail.parent.card_last4}`
                  : "No card on file"}
              </div>
            )}

            <div style={{ fontSize: 12, fontWeight: 700, color: T.textSub, marginBottom: 6 }}>Purchase History</div>
            {(!detail.purchase_history || !detail.purchase_history.length) && (
              <div style={{ color: T.textMuted, fontSize: 13 }}>No orders yet.</div>
            )}
            {detail.purchase_history?.length > 0 && (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ textAlign: "left", color: T.textSub, borderBottom: `1px solid ${T.divider}` }}>
                    <th style={{ padding: "6px 4px" }}>Order</th>
                    <th style={{ padding: "6px 4px" }}>Date</th>
                    <th style={{ padding: "6px 4px" }}>Status</th>
                    <th style={{ padding: "6px 4px", textAlign: "right" }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.purchase_history.map((o) => (
                    <tr key={o.order_id} style={{ borderBottom: `1px solid ${T.divider}` }}>
                      <td style={{ padding: "6px 4px" }}>{o.order_number}</td>
                      <td style={{ padding: "6px 4px" }}>{fmtDateTime(o.created_at)}</td>
                      <td style={{ padding: "6px 4px", textTransform: "capitalize" }}>{o.status}</td>
                      <td style={{ padding: "6px 4px", textAlign: "right" }}>{money(o.grand_total, currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
